import React, { useState, useEffect } from 'react';
import { Typography, Tooltip } from 'antd';
import { WarningOutlined, CheckCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useCurrency } from '../../hooks/useCurrency';
import { CashRegisterDto } from '../../types/cashRegister';

const { Text } = Typography;

interface CashRegisterCardProps {
  cashRegister: CashRegisterDto;
  /** Blind counting: waiter-type roles see no running totals, only meta. */
  hideTotals: boolean;
}

const formatMoney = (currencySymbol: string, amount: number): string =>
  `${currencySymbol} ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const getOperatingTime = (openTime: string): string => {
  const duration = Math.max(0, dayjs().diff(dayjs(openTime), 'minute'));
  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
};

/**
 * Left column of Mi Caja: the financial summary. Hero = expected cash (the
 * number that decides whether the register balances), then sales-by-method
 * proportion bars, then meta. All actions live in the page bar, not here.
 */
const CashRegisterCard: React.FC<CashRegisterCardProps> = ({ cashRegister, hideTotals }) => {
  const { currencySymbol } = useCurrency();

  // Re-render every minute so the operating time stays live
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  const summary = cashRegister.summary;
  const totalMovements = summary?.totalMovements ?? summary?.totalTransactions ?? 0;
  const variance = cashRegister.variance ?? 0;

  const meta = (
    <div className="cash-meta">
      <span>Movimientos <b>{totalMovements}</b></span>
      <span>Tiempo <b>{getOperatingTime(cashRegister.openTime)}</b></span>
    </div>
  );

  if (hideTotals) {
    return (
      <div className="cash-panel">
        <div className="cash-panel__title"><span>Mi caja</span></div>
        <div className="cash-blind-note">
          Los totales están ocultos para el conteo ciego: al cerrar el turno cuentas
          primero y el sistema compara después.
        </div>
        {meta}
      </div>
    );
  }

  // Sales-by-method rows; bar widths are proportions of all sales
  const methods = [
    { label: 'Efectivo', amount: summary?.totalSales ?? 0 },
    { label: 'T. Crédito', amount: summary?.totalCreditCardSales ?? 0 },
    { label: 'T. Débito', amount: summary?.totalDebitCardSales ?? 0 },
    { label: 'Transfer.', amount: summary?.totalTransferSales ?? 0 },
    ...((summary?.totalOtherSales ?? 0) > 0 ? [{ label: 'Otros', amount: summary!.totalOtherSales }] : []),
  ];
  const totalAllSales = summary?.totalAllSales
    ?? methods.reduce((acc, m) => acc + m.amount, 0);

  const varianceClass = Math.abs(variance) < 1
    ? 'cash-hero__diff--ok'
    : Math.abs(variance) < 10
      ? 'cash-hero__diff--warn'
      : 'cash-hero__diff--bad';

  return (
    <div className="cash-panel">
      <div className="cash-hero__label">Efectivo esperado en caja</div>
      <div className="cash-hero__amount">
        {formatMoney(currencySymbol, cashRegister.expectedAmount ?? 0)}
      </div>
      <div className="cash-hero__sub">
        <span>Fondo inicial {formatMoney(currencySymbol, cashRegister.initialAmount ?? 0)}</span>
        {variance !== 0 ? (
          <Tooltip title="Diferencia entre lo contado en el último arqueo y lo esperado">
            <span className={varianceClass}>
              {Math.abs(variance) >= 10 && <WarningOutlined style={{ marginRight: 4 }} />}
              Diferencia {variance > 0 ? '+' : ''}{formatMoney(currencySymbol, variance)}
            </span>
          </Tooltip>
        ) : (summary?.actualAmount != null && (
          <span className="cash-hero__diff--ok">
            <CheckCircleOutlined style={{ marginRight: 4 }} />
            Sin diferencia en último arqueo
          </span>
        ))}
      </div>

      <div className="cash-bk">
        <div className="cash-panel__title" style={{ marginBottom: 4 }}>
          <span>Ventas por método</span>
        </div>
        {methods.map(method => (
          <div key={method.label} className="cash-bk__row">
            <span className="cash-bk__label">{method.label}</span>
            <span className="cash-bk__bar">
              <i style={{ width: totalAllSales > 0 ? `${(method.amount / totalAllSales) * 100}%` : 0 }} />
            </span>
            <span className="cash-bk__amount">
              {formatMoney(currencySymbol, method.amount)}
            </span>
          </div>
        ))}
        <div className="cash-bk__total">
          <Text type="secondary" style={{ fontSize: 13 }}>
            Ventas {formatMoney(currencySymbol, totalAllSales)}
            {(summary?.totalTips ?? 0) > 0 && ` · Propinas ${formatMoney(currencySymbol, summary!.totalTips)}`}
          </Text>
          <b>Ingresos {formatMoney(currencySymbol, summary?.totalRevenue ?? 0)}</b>
        </div>
      </div>

      {meta}
    </div>
  );
};

export default CashRegisterCard;
